import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { ref } from 'vue'

export function useWindMap() {
  const { $isWebViewFlag: isWebViewFlag } = useNuxtApp()
  const runtimeConfig = useRuntimeConfig()
  const uWindData = ref([])
  const vWindData = ref([])
  const markerWindSpeed = ref(0)
  const markerWindDirection = ref(0)
  const showCenterIcon = ref(true)
  const isProgrammaticMove = ref(false)
  let map = null

  const GRID_INFO = ref({})

  const isValidMapCenter = (center, newCenter) => {
    const margin = 0.05
    const minLon = center[0] - margin
    const maxLon = center[0] + margin
    const minLat = center[1] - margin
    const maxLat = center[1] + margin
    return newCenter.lng >= minLon && newCenter.lng <= maxLon && newCenter.lat >= minLat && newCenter.lat <= maxLat
  }

  const calculateDiffValues = () => {
    if (!GRID_INFO.value.WLON || !GRID_INFO.value.ELON
      || !GRID_INFO.value.SLAT || !GRID_INFO.value.NLAT
      || !GRID_INFO.value.IMGW || !GRID_INFO.value.IMGH) {
      console.warn('GRID_INFOに必要なプロパティがありません。updateDataでmetaを指定してください。')
      return
    }

    GRID_INFO.value.DIFF_LON = (GRID_INFO.value.ELON - GRID_INFO.value.WLON) / (GRID_INFO.value.IMGW - 1)
    GRID_INFO.value.DIFF_LAT = (GRID_INFO.value.NLAT - GRID_INFO.value.SLAT) / (GRID_INFO.value.IMGH - 1)
  }

  const colorramp = [
    { value: 1, color: [255, 255, 255, 255] },
    { value: 5, color: [3, 174, 239, 255] },
    { value: 8, color: [5, 173, 80, 255] },
    { value: 12, color: [254, 191, 0, 255] },
    { value: 18, color: [232, 98, 0, 255] },
    { value: 100, color: [254, 0, 0, 255] }
  ]

  const loadBinData = async (url, TypedArray = Float32Array) => {
    return fetch(url).then((res) => res.arrayBuffer()).then((buf) => new TypedArray(buf))
  }

  const addBoundaryBox = (centerPoint) => {
    const margin = 0.05
    const minLon = centerPoint[0] - margin
    const maxLon = centerPoint[0] + margin
    const minLat = centerPoint[1] - margin
    const maxLat = centerPoint[1] + margin
    const boundaryCoordinates = [
      [minLon, minLat],
      [maxLon, minLat],
      [maxLon, maxLat],
      [minLon, maxLat],
      [minLon, minLat]
    ]
    map.addSource('boundary-source', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: boundaryCoordinates
        }
      }
    })
    map.addLayer({
      id: 'boundary-layer',
      type: 'line',
      source: 'boundary-source',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#FF0000',
        'line-width': 1,
        'line-dasharray': [2, 2]
      }
    })
  }

  const setupLayer = async () => {
    map.addSource('arrow', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
    map.addLayer({
      id: 'arrow',
      source: 'arrow',
      type: 'symbol',
      layout: {
        'icon-image': ['get', 'windArrowImage'],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-size': ['interpolate', ['linear'], ['zoom'], 9, 0.2, 15, 0.5],
        'icon-rotate': ['get', 'windDirection']
      },
      paint: {
        'icon-color': [
          'rgb',
          ['to-number', ['get', 'colorR']],
          ['to-number', ['get', 'colorG']],
          ['to-number', ['get', 'colorB']]
        ]
      },
      minzoom: 8
    })

    map.on('moveend', () => {
      updateWindArrow()
    })
  }

  const updateData = async (binaryPath, meta = null) => {
    if (meta) {
      GRID_INFO.value = {
        ...meta
      }
      // DIFF値を再計算
      calculateDiffValues()
    } else if (Object.keys(GRID_INFO.value).length === 0) {
      console.error('GRID_INFOが設定されていません。updateDataの第2引数にmetaを指定してください。')
      return
    }
    try {
    // binaryPathを使用してデータをロード
      const [udata, vdata] = await Promise.all([
        loadBinData(`${runtimeConfig.public.siteDomain}/${binaryPath}_UWIND.bin`),
        loadBinData(`${runtimeConfig.public.siteDomain}/${binaryPath}_VWIND.bin`)
      ])

      uWindData.value = udata
      vWindData.value = vdata
      updateWindArrow()

      // マーカー位置のデータも更新
      if (map) {
        setContent(map.getCenter())
      }
    } catch (error) {
      console.error('風データの読み込みに失敗しました:', error)
    }
  }

  const setupMarker = (firstCenter) => {
    map.on('moveend', () => {
      const currentCenter = map.getCenter()
      if (isValidMapCenter(firstCenter, currentCenter)) {
        setContent(currentCenter)
      } else {
        setTimeout(() => {
          map.jumpTo({
            center: firstCenter,
            duration: 1000,
            essential: true
          })
          showCenterIcon.value = true
        }, 1000)
      }
    })
  }

  const setContent = (center) => {
    const lon = center.lng
    const lat = center.lat
    const uWind = calculateWindAtMarker(uWindData.value, lon, lat)
    const vWind = calculateWindAtMarker(vWindData.value, lon, lat)
    if (uWind === undefined || vWind === undefined) {
      markerWindDirection.value = undefined
      markerWindSpeed.value = undefined
      return
    }
    markerWindDirection.value = (Math.atan2(-uWind, -vWind) * 180 / Math.PI + 180) % 360
    markerWindSpeed.value = Math.sqrt(uWind * uWind + vWind * vWind)
  }

  const calculateWindAtMarker = (data, lon, lat) => {
    const fx = (lon - GRID_INFO.value.WLON) / GRID_INFO.value.DIFF_LON
    const fy = (GRID_INFO.value.NLAT - lat) / GRID_INFO.value.DIFF_LAT

    const dx = Math.floor(fx)
    const dy = Math.floor(fy)

    const rx = fx - dx
    const ry = fy - dy

    if (dx < 0 || dx + 1 >= GRID_INFO.value.IMGW || dy < 0 || dy + 1 >= GRID_INFO.value.IMGH) {
      return undefined
    }
    // 四隅の値を取得（uとv成分）
    const i00 = dy * GRID_INFO.value.IMGW + dx
    const i10 = dy * GRID_INFO.value.IMGW + (dx + 1)
    const i01 = (dy + 1) * GRID_INFO.value.IMGW + dx
    const i11 = (dy + 1) * GRID_INFO.value.IMGW + (dx + 1)

    const wind00 = data[i00]
    const wind10 = data[i10]
    const wind01 = data[i01]
    const wind11 = data[i11]

    // 四隅の重み
    const r00 = (1.0 - rx) * (1.0 - ry) // 左上用の重み
    const r10 = rx * (1.0 - ry) // 右上用の重み
    const r01 = (1.0 - rx) * ry // 左下用の重み
    const r11 = rx * ry // 右下用の重み

    // 欠損値チェックと補間計算
    let validWind = 0
    let totalWeight = 0

    // 左上
    if (wind00 < 9999) {
      validWind += wind00 * r00
      totalWeight += r00
    }
    // 右上
    if (wind10 < 9999) {
      validWind += wind10 * r10
      totalWeight += r10
    }
    // 左下
    if (wind01 < 9999) {
      validWind += wind01 * r01
      totalWeight += r01
    }
    // 右下
    if (wind11 < 9999) {
      validWind += wind11 * r11
      totalWeight += r11
    }
    // 有効なデータがあれば結果を計算
    if (totalWeight > 0) {
      const avgWind = validWind / totalWeight
      return avgWind
    } else {
      return undefined
    }
  }

  const updateWindArrow = () => {
    const step = Math.max(1, Math.min(Math.floor(2 ** Math.ceil(15 - map.getZoom())), 2 ** 4))
    const features = []
    const bbox = map.getBounds()
    for (let y = 0; y < GRID_INFO.value.IMGH; y += step) {
      const lat = GRID_INFO.value.NLAT - y * GRID_INFO.value.DIFF_LAT
      if (lat < bbox.getSouth() || bbox.getNorth() < lat) continue
      for (let x = 0; x < GRID_INFO.value.IMGW; x += step) {
        const lon = GRID_INFO.value.WLON + x * GRID_INFO.value.DIFF_LON
        if (lon < bbox.getWest() || bbox.getEast() < lon) continue
        const i = y * GRID_INFO.value.IMGW + x
        const offset = i
        const u = uWindData.value[offset]
        const v = vWindData.value[offset]
        const windDirection = -Math.atan2(v, u) * 180 / Math.PI + 90
        const windSpeed = Math.sqrt(u * u + v * v)
        if (windSpeed < 0 || 100 < windSpeed) continue
        const getWindArrowImage = (speed) => {
          for (let i = 0; i < colorramp.length; i++) {
            if (speed < colorramp[i].value) {
              return `windArrow${i}`
            }
          }
          return `windArrow${colorramp.length - 1}`
        }

        features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lon, lat]
          },
          properties: {
            windDirection,
            windSpeed,
            windArrowImage: getWindArrowImage(windSpeed)
          }
        })
      }
    }
    map.getSource('arrow').setData({ type: 'FeatureCollection', features })
  }

  const addLocationMarker = async () => {
    const image = await map.loadImage(`${runtimeConfig.public.iconDomain}/golf/image/map/kaze_plot.png`)
    map.addImage('user-location-img', image.data)
    map.addSource('user-location', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [] }
          }
        ]
      }
    })
    map.addLayer({
      id: 'user-location-symbol',
      type: 'symbol',
      source: 'user-location',
      layout: {
        'icon-image': 'user-location-img',
        'icon-size': 0.5,
        'icon-anchor': 'bottom'
      }
    })
  }

  const initMap = async (windMap, center) => {
    let attributionControl = null
    let navigationControl = null
    const mapOptions = {
      container: windMap,
      style: `https://maps.geo.ap-northeast-1.amazonaws.com/v2/styles/Satellite/descriptor?key=${runtimeConfig.public.alsApiKey}`,
      zoom: 13,
      minZoom: 6,
      maxZoom: 16,
      language: 'ja',
      center: center,
      attributionControl: false,
      touchPitch: false
    }

    map = new maplibregl.Map(mapOptions)
    attributionControl = new CustomAttributionControl({
      compact: true
    })
    map.addControl(attributionControl, 'bottom-left')
    navigationControl = new maplibregl.NavigationControl({
      showZoom: true,
      showCompass: false,
      visualizePitch: false,
      visualizeRoll: false
    })
    map.addControl(navigationControl, 'top-left')
    if (isWebViewFlag) {
      map.addControl(new CustomLocationControl(), 'top-left')
    }
    map.dragRotate.disable()
    map.keyboard.disable()
    map.touchZoomRotate.disableRotation()
    map.doubleClickZoom.disable()

    return new Promise((resolve) => {
      map.once('load', async () => {
        map.addSource('gsi-vector', {
          type: 'vector',
          tiles: ['https://cyberjapandata.gsi.go.jp/xyz/experimental_bvmap/{z}/{x}/{y}.pbf'],
          minzoom: 7,
          maxzoom: 16
        })
        // 14-16ズームレベル用のレイヤー
        map.addLayer({
          'id': 'land-use-label-14-16',
          'type': 'symbol',
          'source': 'gsi-vector',
          'source-layer': 'label',
          'minzoom': 14,
          'maxzoom': 16,
          'filter': ['all', ['in', 'ftCode', 100], ['==', 'annoCtg', 531]],
          'layout': {
            'text-field': ['get', 'knj'],
            'text-size': [
              'interpolate',
              ['linear'],
              ['zoom'],
              14, 20,
              15, 22,
              16, 24,
              17, 26
            ],
            'text-pitch-alignment': 'viewport',
            'text-rotation-alignment': 'viewport'
          },
          'paint': {
            'text-color': 'rgba(255,255,255,1)',
            'text-halo-color': 'rgba(0,0,0,1)',
            'text-halo-width': 1
          }
        })

        const arrowSize = 60
        colorramp.forEach((ramp, index) => {
          map.addImage(`windArrow${index}`, {
            width: arrowSize,
            height: arrowSize,
            data: new Uint8Array(arrowSize * arrowSize * 4),
            onAdd: function () {
              const canvas = document.createElement('canvas')
              canvas.width = arrowSize
              canvas.height = arrowSize
              this.context = canvas.getContext('2d', { willReadFrequently: true })
            },
            render: function () {
              const ctx = this.context

              // 矢じりの形状を描画
              ctx.beginPath()
              ctx.moveTo(arrowSize / 2, 0)
              ctx.lineTo(arrowSize / 4, arrowSize)
              ctx.lineTo(arrowSize / 2, arrowSize * 0.75)
              ctx.lineTo(arrowSize * 3 / 4, arrowSize)
              ctx.closePath()
              ctx.fillStyle = `rgb(${ramp.color.slice(0, 3).join(',')})`
              ctx.fill()
              ctx.strokeStyle = 'black'
              ctx.lineWidth = 1

              this.data = ctx.getImageData(0, 0, this.width, this.height).data
              return true
            }
          })
        })
        addBoundaryBox(center)
        await Promise.all([setupLayer(), setupMarker(center)])
        await addLocationMarker()
        map.on('move', () => {
          if (!isProgrammaticMove.value) {
            showCenterIcon.value = true
          }
        })
        map.on('moveend', () => {
          if (isProgrammaticMove.value) {
            isProgrammaticMove.value = false
          }
        })
        setContent(map.getCenter())
        resolve()
      })
    })
  }

  return {
    initMap,
    markerWindSpeed,
    markerWindDirection,
    showCenterIcon,
    isProgrammaticMove,
    updateData
  }
}
